import PropTypes from 'prop-types';

import React from 'react';
import moment from 'moment';
import { Grid, Row, Col, Label, Table } from 'react-bootstrap';
import { humanize, titleize } from 'underscore.string';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import CustomersAPI from 'utils/CustomersAPIUtils';
import PayoutStore from 'stores/accounting/PayoutStore';
import SessionStore from 'stores/SessionStore';
import USER_ROLES from 'constants/UserRoles';
import { withTranslation } from 'react-i18next';
import PaymentHistory from '../../payment-history';
import JobHistory from '../../job-history';
import PaymentSummary from '../../payment-summary';
import {
  getSignedUrl,
  getDocumentsV2,
} from '../../../../../utils/DocumentsAPIUtils';
import { showToast } from '../../../../../stores/ToastStore';
import HistoryTable from '../../history-table';

const getStateFromStores = (id) => {
  const payout = PayoutStore.getPayout(id);
  let balance = 0;
  if (payout) balance = payout.amount_due;
  return {
    payout,
    balance,
    loading: false,
  };
};

function fetchPayout(id) {
  const user = SessionStore.getUser();
  if (user && user._type === USER_ROLES.CARRIER) {
    AccountingActionCreators.getCarrierPayout(user._id, id);
  }
}

const queryOptions = {
  limit: 1,
  order: {
    field: 'createdAt',
    direction: 'desc',
  },
};

class PayoutDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  getInitialState() {
    return {
      loading: true,
    };
  }

  componentDidMount() {
    PayoutStore.register(this.onChange.bind(this));
    fetchPayout(this.props.match.params.id);
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    if (!this.state.payout && nextState.payout) {
      this.populateFundingData(nextState.payout);
    }
  }

  componentWillUnmount() {
    PayoutStore.unregister(this.onChange);
  }

  async onChange() {
    const { t } = this.props;
    const payoutState = getStateFromStores(this.props.match.params.id);
    const { data: documentPayout, errors: documentErrors } =
      await getDocumentsV2([payoutState.payout._id], null, queryOptions);

    if (documentErrors) {
      showToast(
        t('GET_DOCUMENT_ERROR'),
        t('GET_DOCUMENT_FAILED_WITH_ERROR', {
          error: documentErrors[0].message,
        }),
        'error',
      );
    }

    const key = documentPayout?.getDocumentsV2?.documents?.[0]?.key;

    if (key) {
      const { data, errors: signedUrlErrors } = await getSignedUrl(key);

      if (signedUrlErrors) {
        showToast(
          t('GET_SIGNED_URL_ERROR'),
          t('GET_SIGNED_URL_FAILED_WITH_ERROR', {
            error: signedUrlErrors[0].message,
          }),
          'error',
        );
      } else {
        const linkToPayout = data?.getSignedUrl?.signedUrl;
        payoutState.linkToPayout = linkToPayout;
      }
    } else {
      payoutState.linkToPayout = undefined;
    }

    this.setState(payoutState);
  }

  populateFundingData(ref) {
    CustomersAPI.getBankAccounts(ref.carrier._id)
      .then((res) => {
        this.setState((prevState) => {
          const payout = Object.assign({}, prevState.payout);
          const response = res.entity.data;
          payout.carrier.bank_accounts = response.external_accounts;
          return {
            payout,
          };
        });
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  }

  sendEmail() {
    return (to) => {
      AccountingActionCreators.emailPayout(this.state.payout._id, to);
    };
  }

  savePayment() {
    return (payment) => {
      AccountingActionCreators.recordPaymentForPayout(
        this.state.payout._id,
        payment,
      );
    };
  }

  renderDownloadButton() {
    const { t } = this.props;
    if (!this.state.linkToPayout) {
      return '';
    }
    return (
      <div>
        <a href={this.state.linkToPayout} target="_blank">
          {t('DOWNLOAD_PDF')}
        </a>
      </div>
    );
  }

  renderPayout() {
    const payout = this.state.payout;
    const { t } = this.props;
    const className = 'payout row';
    return (
      <div className={className}>
        <Grid className="main-wrapper">
          <Row>
            <Col md={4}>
              <h1>
                <Label bsStyle="success">{payout.number}</Label>
              </h1>
              <br />
              <Table>
                <tbody>
                  <tr>
                    <td>
                      {payout.carrier.name} - {payout.carrier.phone}
                    </td>
                    <td>&nbsp;</td>
                  </tr>
                  <tr>
                    <td>
                      {t('DUE_DATE_WITH_VALUE', {
                        dueDate: moment(payout.due_date).format('L'),
                      })}
                    </td>
                    <td>
                      {t('DATE_CREATED_WITH_VALUE', {
                        dateCreated: moment(payout.date_created).format('L'),
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {t('STATUS_WITH_VALUE', {
                        status: humanize(payout.status),
                      })}
                    </td>
                    <td>&nbsp;</td>
                  </tr>
                  <tr>
                    <td>{this.renderDownloadButton()}</td>
                    <td>&nbsp;</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            <Col md={3} mdOffset={3}>
              <PaymentSummary
                total={payout.total}
                paid={payout.total_payments}
                balance={this.state.balance}
              />
            </Col>
          </Row>
          <hr />
          <Row>
            <Col md={6}>
              <h3 className="my-0 mb-4">{t('PAYMENTS')}</h3>
              <PaymentHistory payments={payout.payments} />
            </Col>
            <Col md={6}>
              {payout.audit && [
                <h3>{titleize(t('HISTORY'))}</h3>,
                <HistoryTable data={payout.audit || []} type="Payout" />,
              ]}
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <h3 className="my-0 mb-4">{titleize(t('JOB', { count: 2 }))}</h3>
              <JobHistory
                context="payout"
                lineItemGroups={payout.lineItemGroups}
                currency={payout?.currency}
              />
            </Col>
          </Row>
        </Grid>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    if (this.state.loading) {
      return <p className="lead">{titleize(t('LOADING'))}</p>;
    }
    return this.renderPayout();
  }
}

PayoutDetails.propTypes = {
  match: PropTypes.object.isRequired,
};

export default withTranslation()(PayoutDetails);
